import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Upload, Clapperboard, CheckCircle2, Loader2, AlertTriangle, ArrowRight } from "lucide-react";

const Stat = ({ icon: Icon, label, value, tone, testid }) => {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    red: "bg-red-50 text-red-700",
    blue: "bg-blue-50 text-blue-700",
  };
  return (
    <div data-testid={testid} className="rounded-lg border border-stone-200 bg-white p-6 transition-all hover:shadow-md hover:-translate-y-0.5">
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tones[tone]}`}><Icon className="h-5 w-5" /></div>
      <p className="mt-4 font-heading text-3xl font-bold text-stone-900">{value}</p>
      <p className="text-sm text-stone-500 mt-1">{label}</p>
    </div>
  );
};

export default function MRDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  const load = async () => {
    try { const res = await api.get("/dashboard/mr"); setData(res.data); } catch (_) {}
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="space-y-8" data-testid="mr-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">Welcome</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-stone-900 mt-1">Hello, {user?.name?.split(" ")[0]}</h1>
        </div>
        <Link to="/mr/upload" data-testid="dashboard-upload-cta"
          className="inline-flex items-center justify-center gap-2 rounded-md bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700">
          <Upload className="h-4 w-4" /> Upload doctor video
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <Stat icon={Clapperboard} label="Total videos" value={data?.total ?? "—"} tone="blue" testid="mr-stat-total" />
        <Stat icon={CheckCircle2} label="Completed" value={data?.completed ?? "—"} tone="emerald" testid="mr-stat-completed" />
        <Stat icon={Loader2} label="In progress" value={data?.processing ?? "—"} tone="amber" testid="mr-stat-processing" />
        <Stat icon={AlertTriangle} label="Failed" value={data?.failed ?? "—"} tone="red" testid="mr-stat-failed" />
      </div>

      <div className="rounded-lg border border-stone-200 bg-white">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
          <h3 className="font-heading text-lg font-semibold text-stone-900">Recent uploads</h3>
          <Link to="/mr/videos" className="text-sm font-medium text-emerald-700 inline-flex items-center gap-1">View all <ArrowRight className="h-4 w-4" /></Link>
        </div>
        {!data ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
        ) : data.recent.length === 0 ? (
          <p className="px-6 py-10 text-center text-stone-500">You haven't uploaded any videos yet.</p>
        ) : (
          <div className="divide-y divide-stone-100">
            {data.recent.map((v) => (
              <div key={v.id} className="flex items-center justify-between px-6 py-4">
                <div><p className="font-medium text-stone-900">{v.doctor_name}</p><p className="text-xs text-stone-500">{v.place || v.doctor_city} · {v.qualification || v.doctor_specialty}</p></div>
                <StatusBadge status={v.status} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
